import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clearStoredAccessKey,
  fetchFluxPromptSampleBuildInfo,
  generateFluxPromptSample,
} from '../utils/fluxPromptSampleApi';
import './FluxPromptSamplePage.css';

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export default function FluxPromptSamplePage() {
  const fileRef = useRef(null);
  const [sourceImage, setSourceImage] = useState('');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resultImage, setResultImage] = useState('');
  const [meta, setMeta] = useState(null);
  const [rev, setRev] = useState('');

  useEffect(() => {
    fetchFluxPromptSampleBuildInfo()
      .then((d) => setRev(d?.rev || ''))
      .catch(() => {});
  }, []);

  const onPickFile = async (file) => {
    if (!file) return;
    setError('');
    try {
      const url = await readFileAsDataUrl(file);
      setSourceImage(url);
      setResultImage('');
      setMeta(null);
    } catch (e) {
      setError(e.message || 'Could not read file');
    }
  };

  const onGenerate = useCallback(async () => {
    if (!sourceImage) {
      setError('Choose an image first.');
      return;
    }
    if (!prompt.trim()) {
      setError('Enter a prompt.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const data = await generateFluxPromptSample({
        image: sourceImage,
        prompt: prompt.trim(),
      });
      setResultImage(data.image || '');
      setMeta(data.meta || null);
    } catch (e) {
      setError(e.message || 'Generation failed');
      setResultImage('');
      setMeta(null);
      if (e.code === 'SAMPLE_ACCESS_DENIED') clearStoredAccessKey();
    } finally {
      setBusy(false);
    }
  }, [sourceImage, prompt]);

  const onSignOut = () => {
    clearStoredAccessKey();
    window.location.reload();
  };

  return (
    <div className="fps-page">
      {busy ? <div className="fps-busy" role="status">Running FLUX…</div> : null}
      <header className="fps-header">
        <div>
          <h1>FLUX prompt sample</h1>
          <p className="fps-sub">
            Standalone preview — no salon or franchise session.{' '}
            <Link to="/flux-reference-transfer" className="fps-link-btn">Reference transfer lab</Link>
          </p>
        </div>
        <div className="fps-header-actions">
          <span className="fps-tag">{rev ? `rev ${rev}` : 'preview'}</span>
          <button type="button" className="fps-link-btn" onClick={onSignOut}>
            Change key
          </button>
        </div>
      </header>

      <div className="fps-grid">
        <section className="fps-panel">
          <h2>Input</h2>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="fps-file"
            onChange={(e) => onPickFile(e.target.files?.[0])}
          />
          <textarea
            className="fps-prompt"
            rows={5}
            placeholder="Describe the edit…"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <button type="button" className="fps-btn" disabled={busy} onClick={onGenerate}>
            Generate
          </button>
          {error ? <p className="fps-error" role="alert">{error}</p> : null}
        </section>

        <section className="fps-panel fps-previews">
          <h2>Preview</h2>
          <div className="fps-pair">
            <figure>
              <figcaption>Source</figcaption>
              {sourceImage ? (
                <img src={sourceImage} alt="Source preview" />
              ) : (
                <div className="fps-placeholder">No image</div>
              )}
            </figure>
            <figure>
              <figcaption>Result</figcaption>
              {resultImage ? (
                <img src={resultImage} alt="FLUX result preview" />
              ) : (
                <div className="fps-placeholder">Run generate</div>
              )}
            </figure>
          </div>
          {meta?.timing_ms?.pipe != null ? (
            <p className="fps-meta">pipe {meta.timing_ms.pipe} ms · storage none</p>
          ) : null}
        </section>
      </div>
    </div>
  );
}
